'use client';

import Link from 'next/link';
import { useState, useEffect, useRef } from 'react';
import { Mail, Menu, Phone, X, Heart, Settings, LogOut, User } from 'lucide-react';
import { NAVIGATION } from './NavLinks';
import { usePathname } from 'next/navigation';
import { useAuthStore } from '@/stores/authStore';
import { userFullName } from '@/lib/user';
import { CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_TEL } from '@/lib/contact';
import { cn } from '@/lib/utils';

export default function MobileNav() {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const { user, isInitialized, logout } = useAuthStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);

  const close = () => setOpen(false);

  // The floating contact affordance is useful while the page is otherwise
  // unobstructed, but it must yield the viewport to the open menu's own
  // contact action. The body attribute lets the independent float hide without
  // coupling its state to this component or changing its closed behavior.
  useEffect(() => {
    if (open) document.body.dataset.mobileNavOpen = '1';
    else delete document.body.dataset.mobileNavOpen;

    return () => {
      delete document.body.dataset.mobileNavOpen;
    };
  }, [open]);

  // Escape and outside-tap can remove the focused menu item from the document.
  // Return focus to the stable toggle so keyboard users never lose their place.
  useEffect(() => {
    if (wasOpenRef.current && !open) toggleRef.current?.focus();
    wasOpenRef.current = open;
  }, [open]);

  // A mobile menu that only closes on the X or a link tap feels stuck. Close it
  // on Escape (keyboard) and on a tap outside the menu; only while it's open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  return (
    <div className="lg:hidden" ref={containerRef}>
      <button
        type="button"
        ref={toggleRef}
        className="site-header__mobile-toggle min-h-11 min-w-11 rounded-lg p-2 text-ink-muted hover:bg-glass hover:text-ink"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls="mobile-navigation"
        aria-label={open ? 'Close menu' : 'Open menu'}
      >
        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      {open && (
        <div
          id="mobile-navigation"
          className="site-mobile-menu glass-blur absolute inset-x-0 z-40 animate-fade-up border-b border-hairline px-4 pt-2"
        >
          <div className="flex flex-col gap-1">
            {NAVIGATION.map((item) => {
              const active = pathname === item.href || pathname?.startsWith(item.href + '/');
              return (
                <Link
                  key={item.name}
                  href={item.href}
                  onClick={close}
                  className={cn(
                    'rounded-xl px-4 py-3 text-base font-medium transition-colors',
                    active ? 'bg-emerald-500/10 text-emerald-300' : 'text-ink-soft hover:bg-glass'
                  )}
                >
                  {item.name}
                </Link>
              );
            })}

            <hr className="my-3 border-hairline" />

            {isInitialized && user ? (
              <>
                <div className="flex items-center gap-3 px-4 py-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/15">
                    <User className="h-4 w-4 text-emerald-300" />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-ink">
                      {userFullName(user) || 'Your account'}
                    </p>
                    <p className="text-xs text-ink-faint">{user.email}</p>
                  </div>
                </div>
                <Link href="/favorites" onClick={close} className="flex items-center gap-3 rounded-xl px-4 py-3 text-ink-soft hover:bg-glass">
                  <Heart className="h-5 w-5" /> Favorites
                </Link>
                <Link href="/settings" onClick={close} className="flex items-center gap-3 rounded-xl px-4 py-3 text-ink-soft hover:bg-glass">
                  <Settings className="h-5 w-5" /> Settings
                </Link>
                <button
                  onClick={() => {
                    logout();
                    close();
                  }}
                  className="flex items-center gap-3 rounded-xl px-4 py-3 text-left text-ink-soft hover:bg-glass"
                >
                  <LogOut className="h-5 w-5" /> Log out
                </button>
              </>
            ) : (
              <div className="flex flex-col gap-2 px-4 py-2">
                <Link
                  href="/login"
                  onClick={close}
                  className="glass w-full rounded-xl px-4 py-3 text-center text-sm font-semibold text-ink hover:bg-glass-bright"
                >
                  Log in
                </Link>
                <Link
                  href="/signup"
                  onClick={close}
                  className="w-full rounded-xl bg-emerald-500 px-4 py-3 text-center text-sm font-semibold text-canvas shadow-glow-emerald hover:bg-emerald-400"
                >
                  Get started
                </Link>
              </div>
            )}

            <hr className="my-3 border-hairline" />
            {CONTACT_PHONE && CONTACT_PHONE_TEL ? (
              <a
                href={CONTACT_PHONE_TEL}
                onClick={close}
                className="flex min-h-11 items-center gap-3 px-4 py-3 font-semibold text-emerald-300"
              >
                <Phone className="h-5 w-5" />
                Call {CONTACT_PHONE}
              </a>
            ) : (
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                onClick={close}
                className="flex min-h-11 items-center gap-3 px-4 py-3 font-semibold text-emerald-300"
              >
                <Mail className="h-5 w-5" />
                Email us
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
